import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type BoardFontOption =
  | 'boogaloo'
  | 'bebas-neue'
  | 'russo-one'
  | 'bungee'
  | 'anton'
  | 'oswald'
  | 'black-ops-one'
  | 'press-start-2p'
  | 'audiowide'
  | 'default';

export interface BoardDisplaySettings {
  boardFont: BoardFontOption;
  /** Multiplier applied to the board content area via CSS `zoom`. 1.0 is
   *  the natural size; less than 1 shrinks (useful for tight fonts like
   *  "Press Start 2P" that otherwise clip), greater than 1 magnifies. */
  boardFontScale: number;
}

/** Bounds for the device-wide font scale. Snapped to the step grid by
 *  {@link BoardDisplaySettingsService.snapAndClamp} so all stored values
 *  land on clean multiples of {@link FONT_SCALE_STEP}. */
const FONT_SCALE_MIN = 0.5;
const FONT_SCALE_MAX = 1.5;
const FONT_SCALE_STEP = 0.1;
const FONT_SCALE_DEFAULT = 1.0;

const DEFAULT_BOARD_DISPLAY_SETTINGS: BoardDisplaySettings = {
  boardFont: 'boogaloo',
  boardFontScale: FONT_SCALE_DEFAULT,
};

@Injectable({
  providedIn: 'root',
})
export class BoardDisplaySettingsService {
  private readonly storageKey = 'crowdctrl-board-display-settings';
  private readonly settingsSubject = new BehaviorSubject<BoardDisplaySettings>(this.loadSettings());

  readonly settings$ = this.settingsSubject.asObservable();

  /** Convenience stream of just the scale, for templates that only care
   *  about the percent label / disabled state. */
  readonly fontScale$: Observable<number> = new BehaviorSubject<number>(
    this.settingsSubject.value.boardFontScale,
  );

  constructor() {
    this.applySettingsToDocument(this.settingsSubject.value);
    this.settings$.subscribe((s) =>
      (this.fontScale$ as BehaviorSubject<number>).next(s.boardFontScale),
    );
  }

  getSettings(): BoardDisplaySettings {
    return this.settingsSubject.value;
  }

  /** Replace settings. Any field omitted falls back to the default. */
  applySettings(settings: Partial<BoardDisplaySettings>): void {
    const merged: BoardDisplaySettings = {
      ...DEFAULT_BOARD_DISPLAY_SETTINGS,
      ...this.settingsSubject.value,
      ...settings,
    };
    merged.boardFontScale = this.snapAndClamp(merged.boardFontScale);
    this.settingsSubject.next(merged);
    this.applySettingsToDocument(merged);
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(merged));
    } catch (error) {
      console.warn('[BoardDisplaySettingsService] Failed to persist settings:', error);
    }
  }

  // -------- Font scale controls (used by board-page FABs) -------------

  getFontScale(): number {
    return this.settingsSubject.value.boardFontScale;
  }

  canIncreaseFontScale(): boolean {
    return this.getFontScale() < FONT_SCALE_MAX;
  }

  canDecreaseFontScale(): boolean {
    return this.getFontScale() > FONT_SCALE_MIN;
  }

  increaseFontScale(): void {
    this.setFontScale(this.getFontScale() + FONT_SCALE_STEP);
  }

  decreaseFontScale(): void {
    this.setFontScale(this.getFontScale() - FONT_SCALE_STEP);
  }

  resetFontScale(): void {
    this.setFontScale(FONT_SCALE_DEFAULT);
  }

  setFontScale(value: number): void {
    const clamped = this.snapAndClamp(value);
    if (clamped === this.settingsSubject.value.boardFontScale) {
      return;
    }
    this.applySettings({ boardFontScale: clamped });
  }

  // -------- Internals --------------------------------------------------

  /** Snap a candidate scale to the nearest {@link FONT_SCALE_STEP} grid
   *  value and clamp into bounds. Avoids floating-point drift when the
   *  user steps up/down repeatedly. */
  private snapAndClamp(value: number): number {
    if (!Number.isFinite(value)) {
      return FONT_SCALE_DEFAULT;
    }
    const stepDecimals = Math.round(1 / FONT_SCALE_STEP);
    const snapped = Math.round(value * stepDecimals) / stepDecimals;
    return Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, snapped));
  }

  private loadSettings(): BoardDisplaySettings {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return { ...DEFAULT_BOARD_DISPLAY_SETTINGS };
      }
      const parsed = JSON.parse(raw) as Partial<BoardDisplaySettings>;
      const merged: BoardDisplaySettings = {
        ...DEFAULT_BOARD_DISPLAY_SETTINGS,
        ...parsed,
      };
      merged.boardFontScale = this.snapAndClamp(merged.boardFontScale);
      return merged;
    } catch (error) {
      console.warn('[BoardDisplaySettingsService] Failed to load settings:', error);
      return { ...DEFAULT_BOARD_DISPLAY_SETTINGS };
    }
  }

  private applySettingsToDocument(settings: BoardDisplaySettings): void {
    const root = document.documentElement;
    root.style.setProperty(
      '--board-font-family',
      this.fontStackFor(settings.boardFont),
    );
    root.style.setProperty(
      '--board-font-scale',
      String(settings.boardFontScale),
    );
  }

  /** CSS font stack for a given option. Each stack ends with a generic
   *  fallback so the board still renders if the Google Font fails to load. */
  private fontStackFor(font: BoardFontOption): string {
    switch (font) {
      case 'boogaloo':
        return "'Boogaloo', 'Roboto', sans-serif";
      case 'bebas-neue':
        return "'Bebas Neue', 'Impact', 'Roboto', sans-serif";
      case 'russo-one':
        return "'Russo One', 'Roboto', sans-serif";
      case 'bungee':
        return "'Bungee', 'Roboto', sans-serif";
      case 'anton':
        return "'Anton', 'Impact', 'Roboto', sans-serif";
      case 'oswald':
        return "'Oswald', 'Roboto', sans-serif";
      case 'black-ops-one':
        return "'Black Ops One', 'Impact', 'Roboto', sans-serif";
      case 'press-start-2p':
        return "'Press Start 2P', 'Courier New', monospace";
      case 'audiowide':
        return "'Audiowide', 'Roboto', sans-serif";
      case 'default':
      default:
        return "var(--ion-font-family, 'Roboto', sans-serif)";
    }
  }
}
